import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  TopToolbar,
  CreateButton,
  ExportButton,
  useListContext,
  ReferenceInput,
  AutocompleteInput,
  DateInput,
  downloadCSV,
} from 'react-admin';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageWrapper } from '../../components/PageWrapper';
import * as XLSX from 'xlsx';

const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);


const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours === 0) {
    return `${minutes}m`;
  }
  
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

const DurationField = ({ source, label }: { source: string; label: string }) => {
  return (
    <FunctionField
      source={source}
      label={label}
      render={(record: any) => (
        <Typography variant="body2">{formatDuration(record[source])}</Typography>
      )}
    />
  );
};


const CustomEmpty = () => {
  return (
    <CustomEmptyState
      resource="driver-time-logs"
      title="No Driver Time Logs yet."
      subtitle="Driver time logs are created automatically through the mobile app when drivers record their hours."
      icon={<TimeIcon sx={{ fontSize: 48, color: 'action.disabled' }} />}
      showBackground={false}
    />
  );
};

export const DriverTimeLogList = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const formatDateValue = React.useCallback((value: any, withTime: boolean) => {
    if (!value) return '';
    const date = new Date(value);
    return withTime ? date.toLocaleString() : date.toLocaleDateString();
  }, []);

  const exporter = React.useCallback((records: any[]) => {
    if (!Array.isArray(records) || records.length === 0) {
      downloadCSV('', 'driver-time-logs.csv');
      return;
    }

    if (isSmall) {
      const headers = ['Date', 'Driving', 'On Duty', 'Off Duty', 'Personal Conveyance'];
      const rows = records.map((r) => [
        formatDateValue(r.startedAt, false),
        formatDuration(Number(r?.durationDriving ?? 0)),
        formatDuration(Number(r?.durationOnDuty ?? 0)),
        formatDuration(Number(r?.durationOffDuty ?? 0)),
        formatDuration(Number(r?.durationPersonalConveyance ?? 0)),
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const csv = XLSX.utils.sheet_to_csv(ws);
      downloadCSV(csv, 'driver-time-logs.csv');
      return;
    }

    const headers = [
      'Started At',
      'Completed At',
      'Completed By',
      'Driving Time',
      'On-Duty Time',
      'Off-Duty Time',
      'Personal Conveyance',
      'Total Shift Time',
    ];
    const rows = records.map((r) => {
      const driving = Number(r?.durationDriving ?? 0);
      const onDuty = Number(r?.durationOnDuty ?? 0);
      const offDuty = Number(r?.durationOffDuty ?? 0);
      const personalConveyance = Number(r?.durationPersonalConveyance ?? 0);
      return [
        formatDateValue(r.startedAt, true),
        formatDateValue(r.submittedAt, true),
        r?.user?.name ?? '',
        formatDuration(driving),
        formatDuration(onDuty),
        formatDuration(offDuty),
        formatDuration(personalConveyance),
        formatDuration(driving + onDuty),
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const csv = XLSX.utils.sheet_to_csv(ws);
    downloadCSV(csv, 'driver-time-logs.csv');
  }, [formatDateValue, isSmall]);

  const driverTimeLogFilters = React.useMemo(() => [
    <ReferenceInput
      key="userId"
      source="userId"
      reference="users"
      label="Driver"
      alwaysOn
      sort={{ field: 'name', order: 'ASC' }}
    >
      <AutocompleteInput
        optionText="name"
        emptyText="All Drivers"
        filterToQuery={searchText => ({ name: searchText })}
        disableClearable={false}
        clearOnBlur
        sx={{
          minWidth: 250,
          '& .MuiOutlinedInput-root': {
            minWidth: 250,
            height: '56px',
          }
        }}
      />
    </ReferenceInput>,
    <DateInput
      key="submittedDateFrom"
      source="submittedDateFrom"
      label="Completed From"
      alwaysOn
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          minWidth: 200,
          height: '56px',
        }
      }}
    />,
    <DateInput
      key="submittedDateTo"
      source="submittedDateTo"
      label="Completed To"
      alwaysOn
      sx={{
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
          minWidth: 200,
          height: '56px',
        }
      }}
    />,
  ], []);

  return (
    <PageWrapper title="Driver Time Logs">
      <List
        actions={<ListActions />}
        sort={{ field: 'startedAt', order: 'DESC' }}
        perPage={25}
        empty={<CustomEmpty />}
        filters={driverTimeLogFilters}
        exporter={exporter}
      >
        {isSmall ? (
          <Datagrid bulkActionButtons={false}>
            <DateField source="startedAt" label="Date" showTime={false} />
            <DurationField source="durationDriving" label="Driving" />
            <DurationField source="durationOnDuty" label="On Duty" />
            <DurationField source="durationOffDuty" label="Off Duty" />
            <DurationField source="durationPersonalConveyance" label="Personal" />
          </Datagrid>
        ) : (
          <Datagrid bulkActionButtons={false} rowClick="show">
            <DateField source="startedAt" label="Started At" showTime />
            <DateField source="submittedAt" label="Completed At" showTime />
            <TextField source="user.name" label="Completed By" />
            <DurationField source="durationDriving" label="Driving" />
            <DurationField source="durationOnDuty" label="On Duty" />
            <DurationField source="durationOffDuty" label="Off Duty" />
            <DurationField source="durationPersonalConveyance" label="Personal Conveyance" />
            <FunctionField
              label="Shift Total"
              render={(record: any) => (
                <Chip
                  label={formatDuration(record.durationDriving + record.durationOnDuty)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Datagrid>
        )}
      </List>
    </PageWrapper>
  );
};